import { EVENT_TYPE_BOOKMARK, EVENT_TYPE_MOTION } from '@solinkcorp/vd-common';
import classNames from 'classnames';
import { ScheduledActivityType } from 'clients/CallhomeApiTypes';
import { type IEvent } from 'clients/PlatformApi';
import drop from 'lodash/drop';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import moment from 'moment-timezone';
import Mustache from 'mustache';
import React from 'react';
import * as EventUtil from 'utils/EventUtil';
import TimeFormatter from 'utils/TimeFormatter';
import { BaseFieldRenderer, type FieldRendererProps } from './base';
import { PrintText } from './printRows';
import { RendererUtil } from './RendererUtil';
import { type FieldRendererField } from './types';
import './videoLinkItems.sass';
import { VideoLinkItemsV2 } from './videoLinkItemsV2';

const MOTION_TEMPLATE = '{{time}}: {{title}}';
const DEFAULT_TEMPLATE = '{{title}}';
const FIXED_WIDTH = 41;
const CAMERA_NAME_MAX_LEN = 30;

const ALARM_OPENED = 'Alarm Opened';

function getVideoLinkItemTemplate(includeTimestamp = true) {
  return (
    `<div class='video-link-item' data-testid='alarm-timeline-row'>` +
    `${includeTimestamp ? "<div class='video-link-time'>{{time}}: </div>" : ''}` +
    `<div class='video-link-description' data-testid='alarm-timeline-description'>{{title}}</div>` +
    `</div>`
  );
}

type VideoLinkItem = NonNullable<IEvent['videoLinkItems']>[0];

function formatAgentOutputValue(value: unknown): string {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (value == null) {
    return '—';
  }
  return String(value);
}

function agentOutputLines(outputs: unknown): string[] {
  if (!outputs || typeof outputs !== 'object' || Array.isArray(outputs)) {
    return [];
  }
  return Object.entries(outputs).map(([key, value]) => `${key}: ${formatAgentOutputValue(value)}`);
}

function printCameraName(
  itemCameraId: string | undefined,
  camerasDictionary: FieldRendererProps['camerasDictionary'],
): string | undefined {
  if (!itemCameraId) {
    return;
  }
  const chCameraId = Object.keys(camerasDictionary).find(
    (id) => camerasDictionary[id].cameraId === itemCameraId,
  );
  return camerasDictionary[chCameraId ?? '']?.name ?? itemCameraId;
}

const toMoment = (value: number | undefined, timeZone: string | undefined) => {
  if (value === undefined) {
    return undefined;
  }

  return timeZone ? moment.tz(value, timeZone) : moment(value);
};

const getFieldValueToDisplay = ({
  fieldValue,
  event,
}: {
  fieldValue: VideoLinkItem[];
  event: IEvent;
}) => {
  const isAlarm = EventUtil.isAlarm(event);
  let fieldValueToDisplay = sortBy(fieldValue, ['timestamp']);

  if (isAlarm) {
    const alarmOpenItemIndex = findIndex(fieldValueToDisplay, ['title', ALARM_OPENED]);
    if (alarmOpenItemIndex >= 1) {
      fieldValueToDisplay = drop(fieldValueToDisplay, alarmOpenItemIndex);
    }
  }
  return fieldValueToDisplay;
};

export class VideoLinkItemsFieldRenderer extends BaseFieldRenderer {
  state = {
    showPrompt: false,
  };

  constructor(props: FieldRendererProps) {
    super(props);

    this.state = {
      showPrompt: props.isVideoPlaying || props?.layoutType === 'WATERFALL',
    };
  }

  render() {
    if (this.props.variant === 'print') {
      return this.renderPrintRows();
    }

    const { event, eventId, fieldMetadata, locale, timezone, activeItem, playerTime, layoutType } =
      this.props;

    // Currently only AI Agent events use the V2 renderer, but ideally this will replace alarms videoLinkItems too in the future
    if (VideoLinkItemsFieldRenderer.shouldUseV2Renderer(event)) {
      return <VideoLinkItemsV2 {...this.props} />;
    }

    const fieldValue = this.props.fieldValue as VideoLinkItem[];

    const fieldValueToDisplay = getFieldValueToDisplay({ fieldValue, event });
    const allowTogglePrompt = layoutType !== 'WATERFALL';

    return (
      <div
        className="receipt"
        id={`receipt-text-${eventId}`}
        onContextMenu={this.onContextMenu}
        style={this.style()}
      >
        {fieldValueToDisplay.map((item, key) => {
          const shouldDisplayPromptToggle =
            key === 0 &&
            fieldValueToDisplay.length >= 2 &&
            EventUtil.isScheduledAIChatEvent(event) &&
            allowTogglePrompt;
          const displayShowPrompt = shouldDisplayPromptToggle && !this.state.showPrompt;
          /** conditionally hide prompt videoLinkItem (always first item) */
          if (displayShowPrompt) {
            return (
              <div
                className="pre"
                onClick={() => this.setState({ showPrompt: true })}
                style={{ marginBottom: '12px' }}
                key={`show-prompt`}
                id={`video-link-item-${key}`}
              >
                <button className="show-prompt-button">{locale.scheduledAIChat.showPrompt}</button>
              </div>
            );
          }
          const cameraId = item.cameraId || event.primaryCameraId;
          const textContent = this.renderEventItemDescription({ fieldMetadata, item });
          const onClick = cameraId
            ? () => this.viewItemInVideo(cameraId, item.timestamp)
            : undefined;
          const prevItem = fieldValueToDisplay[key - 1];
          const isDateDiffWithPrevItem =
            prevItem && !moment(prevItem.timestamp).isSame(item.timestamp, 'day');
          // Check that the playerTime is within the duration of the event
          const eventInProgress =
            playerTime &&
            playerTime > event.startTime.valueOf() &&
            playerTime < event.endTime.valueOf();
          // Check if the videoLinkItem is before the current active item
          // The activeItem will remain active for 1 second after it has past
          const isBeforeActiveItem =
            !activeItem || (activeItem.time && item.timestamp && item.timestamp < activeItem.time);
          const timestampIsInPast = playerTime && item.timestamp && item.timestamp < playerTime;
          const itemLinkClass = classNames('item-link', {
            highlighted: item.timestamp === activeItem?.time,
            past: timestampIsInPast && eventInProgress && isBeforeActiveItem,
          });

          return (
            <React.Fragment key={`video-link-items-fragment-${key}`}>
              {isDateDiffWithPrevItem && (
                <div className="date-separator" key={`date-separator-${key}`}>
                  {toMoment(item.timestamp, timezone)?.format(locale.date.format1)}
                </div>
              )}
              <div className="pre" key={`pre-${key}`}>
                <a
                  className={itemLinkClass}
                  dangerouslySetInnerHTML={{
                    __html: RendererUtil.makeLineFixedWidth(FIXED_WIDTH, textContent).join('\n'),
                  }}
                  onClick={onClick}
                ></a>
              </div>
              {/* action to hide prompt */}
              {shouldDisplayPromptToggle ? (
                <div
                  className="pre"
                  onClick={() => this.setState({ showPrompt: false })}
                  style={{ marginTop: '6px', marginBottom: '12px' }}
                  key={`hide-prompt`}
                >
                  <button className="show-prompt-button">
                    {locale.scheduledAIChat.hidePrompt}
                  </button>
                </div>
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  renderPrintRows(): JSX.Element | null {
    const { event, timezone, locale, camerasDictionary } = this.props;
    const fieldValue = this.props.fieldValue as VideoLinkItem[];
    if (!Array.isArray(fieldValue) || fieldValue.length === 0) {
      return null;
    }

    const items = getFieldValueToDisplay({ fieldValue, event });
    const timeFormat = TimeFormatter.getString({ showTimezone: false });
    const showTime = !EventUtil.isScheduledAIChatEvent(event);
    const isAgentEvent = VideoLinkItemsFieldRenderer.shouldUseV2Renderer(event);
    const outputLines = isAgentEvent ? agentOutputLines(event.details?.agentOutputs) : [];

    return (
      <div className="flex flex-col gap-2">
        {outputLines.map((line) => (
          <PrintText key={line}>{line}</PrintText>
        ))}
        {items.map((item, key) => {
          const prevItem = items[key - 1];
          const isDateDiffWithPrevItem =
            prevItem && !moment(prevItem.timestamp).isSame(item.timestamp, 'day');
          const time = toMoment(item.timestamp, timezone)?.format(timeFormat) ?? '';
          const title = (item.title ?? '').replace(/\*\*(.*?)\*\*/g, '$1');
          const summary = item.metadata?.summary as string | undefined;
          const description = item.metadata?.description;
          const cameraName = isAgentEvent
            ? printCameraName(item.cameraId, camerasDictionary)
            : undefined;

          return (
            <React.Fragment key={`print-video-link-${key}`}>
              {isDateDiffWithPrevItem ? (
                <PrintText>
                  {toMoment(item.timestamp, timezone)?.format(locale.date.format1) ?? ''}
                </PrintText>
              ) : null}
              <PrintText data-testid="alarm-timeline-row">
                {showTime && time ? `${time}: ${title}` : title}
              </PrintText>
              {summary ? <PrintText>{summary}</PrintText> : null}
              {isAgentEvent && description ? <PrintText>{description}</PrintText> : null}
              {cameraName ? <PrintText>{cameraName}</PrintText> : null}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  renderEventItemDescription({
    fieldMetadata,
    item,
  }: {
    fieldMetadata: FieldRendererField;
    item: VideoLinkItem;
  }) {
    const { event } = this.props;
    const isScheduledAIChat =
      event.type?.toLowerCase() === EVENT_TYPE_BOOKMARK &&
      event.subtype?.toLowerCase() === ScheduledActivityType.SCHEDULED_AI_CHAT;

    if (EventUtil.isAlarm(event) || event.videoLinkItems?.length) {
      const rendered = this.renderVideoLinkItems({ item });
      const renderedWithNewLines = rendered.replace(/\n/g, '<br>');
      if (isScheduledAIChat) {
        return renderedWithNewLines.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      }
      return renderedWithNewLines;
    }
    if (event.type?.toLowerCase() === EVENT_TYPE_MOTION) {
      return this.renderMotionEventItemDescription(item);
    }

    const template = fieldMetadata.template || DEFAULT_TEMPLATE;
    const _item = {
      ...item,
      cameraId: this.getCameraName(item),
      date: moment(item.timestamp).format('h:mm:ss'),
    };
    return Mustache.render(template, _item);
  }

  renderVideoLinkItems({ item }: { item: VideoLinkItem }) {
    const { timezone, event } = this.props;
    const { timestamp } = item;

    const timeFormat = TimeFormatter.getString({ showTimezone: false });

    const _item = { ...item, time: toMoment(timestamp, timezone)?.format(timeFormat) };

    return Mustache.render(
      getVideoLinkItemTemplate(!EventUtil.isScheduledAIChatEvent(event)),
      _item,
    );
  }

  renderMotionEventItemDescription(item: VideoLinkItem) {
    const metadataAi = get(item, 'metadata.ai', {});
    const metadataState = get(item, 'metadata.state', '');
    const state = metadataState.toUpperCase() === 'OPEN' ? ` [${metadataState}]` : '';
    const metadataDuration = get(item, 'metadata.duration');
    const duration = metadataDuration && RendererUtil.timeFormat(metadataDuration * 1000).trim();
    const ai = metadataAi.people || metadataAi.person || metadataAi.vehicle ? ' [AI]' : '';
    const { timezone } = this.props;
    const { timestamp } = item;
    const timeFormat = TimeFormatter.getString({ showTimezone: false });
    const cameraId = this.getCameraName(item);

    const _item = {
      title: `${cameraId}${state}${ai}\t ${duration}`,
      ...item,
      cameraId,
      date: moment(timestamp).format('h:mm:ss'),
      duration,
      ai,
      time: toMoment(timestamp, timezone)?.format(timeFormat),
      state,
    };
    return Mustache.render(MOTION_TEMPLATE, _item);
  }

  getCameraName(item: VideoLinkItem) {
    const { camerasDictionary, event } = this.props;
    const itemCameraId = item.cameraId || event.primaryCameraId;
    const cameraUId = Object.keys(camerasDictionary).find(
      (c) => camerasDictionary[c].cameraId === itemCameraId,
    );
    const cameraName = cameraUId ? camerasDictionary[cameraUId].name || itemCameraId : itemCameraId;
    let maxLen = CAMERA_NAME_MAX_LEN;
    if (get(item, 'metadata.ai')) {
      maxLen -= 5;
    }
    if (get(item, 'metadata.state', '').toUpperCase() === 'OPEN') {
      maxLen -= 7;
    }
    if (cameraName && cameraName.length > maxLen) {
      return cameraName.slice(0, maxLen - 1) + '…';
    }
    return cameraName;
  }

  viewItemInVideo = (cameraId: string, time: number | undefined) => {
    const { event, onEventRowClick } = this.props;
    onEventRowClick(event, cameraId, time);
  };

  onContextMenu = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    this.props.onContextMenu(event);
  };

  static shouldUseV2Renderer(event: IEvent): boolean {
    return event.subtype === 'agent';
  }
}
